/**
 * UploadAndCropComponent - UNIFIED SINGLE SOURCE OF TRUTH
 * =========================================================
 * 
 * GOLD STANDARD: Based on working ImageEditor using react-image-crop
 * 
 * RULES (NON-NEGOTIABLE):
 * 1. Upload → Crop Editor opens AUTOMATICALLY
 * 2. Crop is MANDATORY - cannot be skipped
 * 3. Output is ALWAYS slab_clean (cropped, validated)
 * 4. No duplicate uploaders allowed anywhere
 * 
 * INPUT:
 * - mode: "slab" | "room" | "material"
 * - onComplete: callback with result
 * 
 * OUTPUT:
 * {
 *   original_image_url: string,
 *   slab_clean_url: string,
 *   crop_coordinates: { x, y, width, height },
 *   resolution: { width, height },
 *   blob: Blob
 * }
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { 
  Upload, Crop, Sun, RotateCcw, Check, X, Loader2,
  ZoomIn, ZoomOut, FlipHorizontal, FlipVertical, AlertTriangle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UploadAndCropComponent = ({
  mode = 'slab', // 'slab' | 'room' | 'material'
  projectId: initialProjectId,
  onComplete,
  onCancel,
  className = '',
  // Style variants
  variant = 'dark' // 'dark' | 'light'
}) => {
  // State machine: 'idle' -> 'editing' -> 'uploading' -> 'complete'
  const [state, setState] = useState('idle');
  const [projectId, setProjectId] = useState(initialProjectId);
  const [originalFile, setOriginalFile] = useState(null);
  const [originalDataUrl, setOriginalDataUrl] = useState(null);
  const [error, setError] = useState(null);
  
  // Crop state (using react-image-crop)
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [activeTab, setActiveTab] = useState('crop');
  const [isCropping, setIsCropping] = useState(true); // Always start in crop mode
  
  // Refs
  const fileInputRef = useRef(null);
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  
  // Colors based on variant
  const colors = variant === 'dark' ? {
    bg: 'bg-[#121212]',
    bgSecondary: 'bg-[#1A1A1A]',
    bgTertiary: 'bg-[#0A0A0A]',
    border: 'border-[#2A2A2A]',
    text: 'text-white',
    textMuted: 'text-[#A0A0A0]',
    textDim: 'text-[#666666]',
    accent: 'bg-[#D6D6D6] hover:bg-white text-[#121212]',
    success: 'text-[#4ADE80]',
    error: 'text-[#EF4444]'
  } : {
    bg: 'bg-white',
    bgSecondary: 'bg-stone-50',
    bgTertiary: 'bg-stone-100',
    border: 'border-stone-200',
    text: 'text-stone-900',
    textMuted: 'text-stone-500',
    textDim: 'text-stone-400',
    accent: 'bg-stone-900 hover:bg-stone-800 text-white',
    success: 'text-green-600',
    error: 'text-red-600'
  };
  
  // Auto-detect state
  const [isDetecting, setIsDetecting] = useState(false);
  const [autoDetectResult, setAutoDetectResult] = useState(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  
  // Ensure project exists (for SRE mode)
  const ensureProject = async () => {
    if (projectId) return projectId;
    if (mode === 'material') return null; // No project needed for material mode
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/sre/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: `SRE Project ${new Date().toISOString().slice(0, 10)}` }),
      });
      
      if (!response.ok) throw new Error('Failed to create project');
      const data = await response.json();
      setProjectId(data.project_id);
      return data.project_id;
    } catch (err) {
      throw new Error('Failed to create project: ' + err.message);
    }
  };

  // =============================================
  // AUTO DETECT SLAB (Computer Vision)
  // =============================================
  const autoDetectSlab = useCallback(async (imageDataUrl) => {
    if (mode === 'room') {
      // Skip auto-detect for room images
      return null;
    }
    
    setIsDetecting(true);
    console.log('AUTO DETECT: Starting slab detection...');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/sre/auto-detect-slab`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_base64: imageDataUrl,
          method: 'combined'
        }),
      });
      
      if (!response.ok) {
        console.warn('AUTO DETECT: API call failed');
        return null;
      }
      
      const result = await response.json();
      console.log('AUTO DETECT RESULT:', result);
      
      if (result.success && result.found && result.confidence > 0.5) {
        return result;
      }
      
      return null;
    } catch (err) {
      console.warn('AUTO DETECT: Error', err);
      return null;
    } finally {
      setIsDetecting(false);
    }
  }, [mode]);

  // =============================================
  // FILE SELECTION → AUTO DETECT → OPEN CROP
  // =============================================
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Use JPG, PNG, or WebP.');
      return;
    }
    
    // Validate file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      setError('File too large. Maximum 100MB.');
      return;
    }
    
    setError(null);
    setOriginalFile(file);
    
    // Read file as DataURL
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUrl = ev.target?.result;
      setOriginalDataUrl(dataUrl);
      
      console.log('UPLOAD ORIGINAL:', file.name, file.size, 'bytes');
      
      // Open editor in "detecting" state
      setState('editing');
      setIsDetecting(true);
      
      // Get image dimensions
      const img = new Image();
      img.onload = async () => {
        setImageSize({ width: img.width, height: img.height });
        
        // Try auto-detect slab for slab mode
        if (mode === 'slab' || mode === 'material') {
          const detection = await autoDetectSlab(dataUrl);
          
          if (detection && detection.crop_box) {
            // Apply auto-detected crop
            setAutoDetectResult(detection);
            setCrop({
              unit: '%',
              x: detection.crop_box.x * 100,
              y: detection.crop_box.y * 100,
              width: detection.crop_box.width * 100,
              height: detection.crop_box.height * 100
            });
            console.log('AUTO CROP APPLIED:', detection.crop_box);
          } else {
            // Fallback to 90% default
            setCrop({ unit: '%', width: 90, height: 90, x: 5, y: 5 });
          }
        } else {
          // Room mode - full image
          setCrop({ unit: '%', width: 95, height: 95, x: 2.5, y: 2.5 });
        }
        
        setIsDetecting(false);
        setIsCropping(true);
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };

  // =============================================
  // FILTER STYLE
  // =============================================
  const getFilterStyle = () => ({
    filter: `brightness(${brightness}%) contrast(${contrast}%)`,
    transform: `rotate(${rotation}deg) scaleX(${flipH ? -1 : 1}) scaleY(${flipV ? -1 : 1})`,
  });

  // =============================================
  // RESET
  // =============================================
  const resetAll = () => {
    setCrop({ unit: '%', width: 90, height: 90, x: 5, y: 5 });
    setCompletedCrop(null);
    setBrightness(100);
    setContrast(100);
    setRotation(0);
    setFlipH(false);
    setFlipV(false);
    setIsCropping(true);
  };

  // =============================================
  // APPLY CROP AND GENERATE slab_clean
  // =============================================
  const applyCropAndFilters = useCallback(() => {
    const image = imgRef.current;
    const canvas = canvasRef.current;
    
    if (!image || !canvas) return null;

    const ctx = canvas.getContext('2d');
    
    // Determine source dimensions (cropped or full)
    let sx = 0, sy = 0, sWidth = image.naturalWidth, sHeight = image.naturalHeight;
    
    if (completedCrop && completedCrop.width && completedCrop.height) {
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      sx = completedCrop.x * scaleX;
      sy = completedCrop.y * scaleY;
      sWidth = completedCrop.width * scaleX;
      sHeight = completedCrop.height * scaleY;
    }

    // Set canvas size
    canvas.width = sWidth;
    canvas.height = sHeight;

    // Apply transformations
    ctx.save();
    
    // Handle rotation and flips
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
    ctx.translate(-canvas.width / 2, -canvas.height / 2);
    
    // Apply brightness and contrast via filter
    ctx.filter = `brightness(${brightness}%) contrast(${contrast}%)`;
    
    // Draw the image
    ctx.drawImage(
      image,
      sx, sy, sWidth, sHeight,
      0, 0, sWidth, sHeight
    );
    
    ctx.restore();

    console.log('SLAB CLEAN generated:', sWidth, 'x', sHeight, 'px');
    console.log('CROP COORDINATES:', { x: sx, y: sy, width: sWidth, height: sHeight });

    return {
      dataUrl: canvas.toDataURL('image/jpeg', 0.95),
      width: sWidth,
      height: sHeight,
      cropCoords: { x: sx, y: sy, width: sWidth, height: sHeight }
    };
  }, [completedCrop, brightness, contrast, rotation, flipH, flipV]);

  // =============================================
  // CONFIRM AND UPLOAD
  // =============================================
  const handleConfirm = useCallback(async () => {
    // HARD VALIDATION: Crop is required
    if (!completedCrop || !completedCrop.width || !completedCrop.height) {
      setError('SLAB_CLEAN_REQUIRED - Please select crop area');
      return;
    }
    
    setState('uploading');
    setError(null);
    
    try {
      const result = applyCropAndFilters();
      if (!result) {
        throw new Error('Failed to generate cropped image');
      }
      
      // Convert to blob
      const response = await fetch(result.dataUrl);
      const blob = await response.blob();
      
      // For SRE mode, upload to backend
      if (mode === 'slab' || mode === 'room') {
        const currentProjectId = await ensureProject();
        
        const formData = new FormData();
        formData.append('file', new File([blob], 'slab_clean.jpg', { type: 'image/jpeg' }));
        formData.append('project_id', currentProjectId);
        formData.append('image_type', mode);
        
        const uploadResponse = await fetch(`${BACKEND_URL}/api/sre/upload`, {
          method: 'POST',
          body: formData,
        });
        
        if (!uploadResponse.ok) {
          const errData = await uploadResponse.json().catch(() => ({}));
          throw new Error(errData.detail || errData.error || 'Upload failed');
        }
        
        const uploadData = await uploadResponse.json();
        
        console.log('UPLOAD SUCCESS - Image ID:', uploadData.image_id);
        
        // Build result object
        const finalResult = {
          original_image_url: originalDataUrl,
          slab_clean_url: result.dataUrl,
          crop_coordinates: result.cropCoords,
          resolution: { width: result.width, height: result.height },
          image_id: uploadData.image_id,
          blob: blob
        };
        
        setState('complete');
        
        if (onComplete) {
          onComplete(finalResult, result.dataUrl, blob);
        }
      } else {
        // Material mode - just return the cropped image
        const finalResult = {
          original_image_url: originalDataUrl,
          slab_clean_url: result.dataUrl,
          crop_coordinates: result.cropCoords,
          resolution: { width: result.width, height: result.height },
          blob: blob
        };
        
        setState('complete');
        
        if (onComplete) {
          onComplete(finalResult, result.dataUrl, blob);
        }
      }
      
    } catch (err) {
      console.error('UPLOAD ERROR:', err);
      setError(`Upload failed: ${err.message}`);
      setState('editing');
    }
  }, [applyCropAndFilters, completedCrop, mode, onComplete, originalDataUrl, ensureProject]);

  // =============================================
  // RENDER: IDLE STATE (File selector)
  // =============================================
  if (state === 'idle') {
    return (
      <div className={`${className}`} data-testid="upload-and-crop-idle">
        <button
          onClick={() => fileInputRef.current?.click()}
          className={`w-full p-12 border-2 border-dashed ${colors.border} rounded-lg hover:border-[#3A3A3A] transition-colors group ${colors.bgSecondary}`}
          data-testid={`sre-upload-${mode}`}
        >
          <Upload className={`w-12 h-12 mx-auto mb-4 ${colors.textDim} group-hover:${colors.textMuted} transition-colors`} />
          <span className={`${colors.textMuted} group-hover:${colors.text} transition-colors block mb-2`}>
            Click to select {mode === 'slab' ? 'slab texture' : mode === 'room' ? 'environment image' : 'material image'}
          </span>
          <span className={`${colors.textDim} text-xs`}>
            JPG, PNG, WebP • Max 100MB
          </span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileSelect}
            className="hidden"
            data-testid={`sre-file-input-${mode}`}
          />
        </button>
        
        {error && (
          <div className={`mt-4 p-3 ${colors.bgSecondary} border border-red-500/30 rounded flex items-center gap-2 ${colors.error} text-sm`}>
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}
      </div>
    );
  }

  // =============================================
  // RENDER: EDITING STATE (Crop editor - MANDATORY)
  // =============================================
  if (state === 'editing') {
    return (
      <div 
        className={`fixed inset-0 ${colors.bg} z-50 flex flex-col`}
        data-testid="upload-and-crop-editor"
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${colors.border} ${colors.bgSecondary}`}>
          <div>
            <h3 className={`text-lg font-medium ${colors.text}`}>
              {mode === 'slab' ? 'Crop Slab Texture' : mode === 'room' ? 'Crop Environment' : 'Crop Image'}
            </h3>
            <p className={`text-xs ${colors.textMuted}`}>
              {isDetecting 
                ? 'Auto-detecting slab boundaries...' 
                : autoDetectResult?.found 
                  ? `Auto-detected with ${Math.round(autoDetectResult.confidence * 100)}% confidence • Adjust if needed`
                  : 'Select the area to use • Crop is REQUIRED'
              }
            </p>
          </div>
          <div className="flex items-center gap-2">
            {autoDetectResult?.found && (
              <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded">
                AUTO
              </span>
            )}
            <Button variant="ghost" size="sm" onClick={resetAll} className={colors.textMuted}>
              <RotateCcw className="w-4 h-4 mr-1" /> Reset
            </Button>
            <Button variant="outline" size="sm" onClick={onCancel}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Tools Sidebar */}
          <div className={`w-64 border-r ${colors.border} ${colors.bgSecondary} p-4 space-y-6 overflow-y-auto`}>
            {/* Tab Selector */}
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('crop')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'crop' 
                    ? 'bg-stone-900 text-white' 
                    : `${colors.bgTertiary} border ${colors.border} hover:bg-opacity-80`
                }`}
              >
                <Crop className="w-4 h-4 mx-auto mb-1" />
                Crop
              </button>
              <button
                onClick={() => setActiveTab('adjust')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'adjust' 
                    ? 'bg-stone-900 text-white' 
                    : `${colors.bgTertiary} border ${colors.border} hover:bg-opacity-80`
                }`}
              >
                <Sun className="w-4 h-4 mx-auto mb-1" />
                Adjust
              </button>
            </div>

            {/* Crop Tools */}
            {activeTab === 'crop' && (
              <div className="space-y-4">
                {/* Auto-Detection Status */}
                {isDetecting && (
                  <div className={`p-3 ${colors.bgTertiary} rounded border ${colors.border}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Loader2 className="w-4 h-4 animate-spin text-[var(--gold)]" />
                      <span className={`text-sm ${colors.text}`}>Auto-detecting slab...</span>
                    </div>
                    <p className={`text-xs ${colors.textMuted}`}>
                      Analyzing edges and contours
                    </p>
                  </div>
                )}
                
                {autoDetectResult?.found && !isDetecting && (
                  <div className="p-3 bg-green-500/10 rounded border border-green-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Check className="w-4 h-4 text-green-400" />
                      <span className="text-sm text-green-400">Slab Auto-Detected</span>
                    </div>
                    <p className={`text-xs ${colors.textMuted}`}>
                      Confidence: {Math.round(autoDetectResult.confidence * 100)}%
                    </p>
                    {autoDetectResult.rotation_angle !== 0 && (
                      <p className={`text-xs ${colors.textMuted} mt-1`}>
                        Suggested rotation: {autoDetectResult.rotation_angle.toFixed(1)}°
                      </p>
                    )}
                  </div>
                )}
                
                <div>
                  <p className={`text-sm ${colors.textMuted} mb-3`}>
                    {autoDetectResult?.found 
                      ? 'Adjust the selection if needed'
                      : 'Drag to select crop area'
                    }
                  </p>
                  {completedCrop && completedCrop.width > 0 && (
                    <div className={`p-2 ${colors.bgTertiary} rounded text-xs ${colors.textMuted}`}>
                      Selected: {Math.round(completedCrop.width)}×{Math.round(completedCrop.height)} px
                    </div>
                  )}
                </div>

                {/* Rotation */}
                <div>
                  <label className={`text-sm font-medium mb-2 block ${colors.text}`}>
                    Rotation: {rotation}°
                  </label>
                  <Slider
                    value={[rotation]}
                    onValueChange={([v]) => setRotation(v)}
                    min={-180}
                    max={180}
                    step={1}
                  />
                  <div className="flex gap-2 mt-2">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => setRotation(r => r - 90)}>-90°</Button>
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => setRotation(0)}>0°</Button>
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => setRotation(r => r + 90)}>+90°</Button>
                  </div>
                </div>

                {/* Flip */}
                <div>
                  <label className={`text-sm font-medium mb-2 block ${colors.text}`}>Flip</label>
                  <div className="flex gap-2">
                    <Button 
                      variant={flipH ? "default" : "outline"} 
                      size="sm" 
                      className="flex-1"
                      onClick={() => setFlipH(!flipH)}
                    >
                      <FlipHorizontal className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant={flipV ? "default" : "outline"} 
                      size="sm" 
                      className="flex-1"
                      onClick={() => setFlipV(!flipV)}
                    >
                      <FlipVertical className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Adjustment Tools */}
            {activeTab === 'adjust' && (
              <div className="space-y-6">
                {/* Brightness */}
                <div>
                  <label className={`text-sm font-medium mb-2 flex items-center justify-between ${colors.text}`}>
                    <span className="flex items-center gap-2"><Sun className="w-4 h-4" /> Brightness</span>
                    <span className={colors.textMuted}>{brightness}%</span>
                  </label>
                  <Slider value={[brightness]} onValueChange={([v]) => setBrightness(v)} min={50} max={150} step={1} />
                </div>

                {/* Contrast */}
                <div>
                  <label className={`text-sm font-medium mb-2 flex items-center justify-between ${colors.text}`}>
                    <span className="flex items-center gap-2"><ZoomIn className="w-4 h-4" /> Contrast</span>
                    <span className={colors.textMuted}>{contrast}%</span>
                  </label>
                  <Slider value={[contrast]} onValueChange={([v]) => setContrast(v)} min={50} max={150} step={1} />
                </div>

                {/* Quick Presets */}
                <div>
                  <label className={`text-sm font-medium mb-2 block ${colors.text}`}>Presets</label>
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setBrightness(110); setContrast(110); }}>Vivid</Button>
                    <Button variant="outline" size="sm" onClick={() => { setBrightness(90); setContrast(120); }}>Darker</Button>
                    <Button variant="outline" size="sm" onClick={() => { setBrightness(115); setContrast(95); }}>Bright</Button>
                    <Button variant="outline" size="sm" onClick={() => { setBrightness(100); setContrast(100); }}>Original</Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Image Preview */}
          <div className={`flex-1 p-6 ${colors.bgTertiary} flex items-center justify-center overflow-auto`}>
            <div className="relative max-w-full max-h-[80vh]">
              {originalDataUrl && (
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  className="max-h-[70vh]"
                >
                  <img
                    ref={imgRef}
                    src={originalDataUrl}
                    alt="Edit"
                    style={getFilterStyle()}
                    className="max-h-[70vh] w-auto"
                  />
                </ReactCrop>
              )}
            </div>
          </div>
        </div>

        {/* Footer with Confirm */}
        <div className={`px-6 py-4 border-t ${colors.border} ${colors.bgSecondary}`}>
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            {error && (
              <div className={`flex items-center gap-2 ${colors.error} text-sm`}>
                <AlertTriangle className="w-4 h-4" />
                {error}
              </div>
            )}
            <div className="flex-1" />
            <Button
              onClick={handleConfirm}
              disabled={!completedCrop || !completedCrop.width}
              className={`px-8 py-3 ${colors.accent} font-medium`}
              data-testid="crop-confirm-btn"
            >
              <Check className="w-5 h-5 mr-2" />
              Confirm & Generate slab_clean
            </Button>
          </div>
        </div>

        {/* Hidden canvas for processing */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    );
  }

  // =============================================
  // RENDER: UPLOADING STATE
  // =============================================
  if (state === 'uploading') {
    return (
      <div 
        className={`fixed inset-0 ${colors.bg} z-50 flex items-center justify-center`}
        data-testid="upload-and-crop-uploading"
      >
        <div className="text-center">
          <Loader2 className={`w-12 h-12 ${colors.textMuted} animate-spin mx-auto mb-4`} />
          <p className={colors.textMuted}>Generating slab_clean...</p>
          <p className={`${colors.textDim} text-xs mt-2`}>Uploading to server</p>
        </div>
      </div>
    );
  }

  // =============================================
  // RENDER: COMPLETE STATE
  // =============================================
  if (state === 'complete') {
    return (
      <div 
        className={`p-4 ${colors.bgSecondary} border border-green-500/30 rounded`}
        data-testid="upload-and-crop-complete"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
            <Check className="w-5 h-5 text-green-500" />
          </div>
          <div>
            <p className={`${colors.text} font-medium`}>
              {mode === 'slab' ? 'Slab texture' : mode === 'room' ? 'Environment' : 'Image'} ready
            </p>
            <p className={`${colors.textDim} text-xs`}>slab_clean generated successfully</p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default UploadAndCropComponent;
